<template>
  <section
    data-cy="settings-view"
    :data-view-ready="!settingsLoading">
    <div
      v-if="settingsLoading"
      class="col-12 text-center">
      <coozzy-spinner class="m-3" />
    </div>
    <div
      v-else
      class="row">
      <div class="col-12 customTabs mb-3">
        <b-tabs
          v-model="tabIndex"
          lazy>
          <b-tab
            v-if="isCompanyAdmin">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('general')">
                <span>{{ $t('message.settings.general') }}</span>
              </router-link>
            </template>
            <company-profile-settings
              v-if="isCompanyAccount || isPlatformEmployee"
              @has-changed="hasChangedEdited" />
            <individual-profile-settings v-else />
            <edit-region-settings />
          </b-tab>
          <b-tab
            v-if="(hasAccessMarketBase || isPlatformEmployee) && isEditor">
            <template #title>
              <router-link
                data-cy="settings-tab-marketbase"
                :to="getSettingRouteByKey('marketBase')">
                <span>MarketBase</span>
              </router-link>
            </template>
            <export-settings :account-id="accountId" />
            <application-form-settings />
            <automatic-reference-check-settings v-if="isCompanyAdmin" />
            <application-dossier-settings v-if="isCompanyAdmin" />
            <affordability-settings v-if="isCompanyAdmin" />
            <reference-email-templates-settings v-if="isCompanyAdmin" />
            <appointment-reminders-settings v-if="isCompanyAdmin" />
            <viewing-appointment-sender-email-settings v-if="isCompanyAdmin" />
          </b-tab>
          <b-tab>
            <template #title>
              <router-link
                data-cy="settings-tab-employees"
                :to="getSettingRouteByKey('employees')">
                <span>{{ $t('message.settings.employees') }}</span>
              </router-link>
            </template>
            <employee-settings v-if="isEditor" />
            <edit-password-settings />
          </b-tab>
          <b-tab
            v-if="isEditor || (isCompanyAdmin && (hasAccessPropBase || hasAccessMarketBase || hasAccessAdminBase || hasAccessAssetBase || hasAccessAccountingBase || isPlatformEmployee))">
            <template #title>
              <router-link
                data-cy="settings-tab-messages"
                :to="getSettingRouteByKey('messages')">
                <span>{{ $t('message.settings.messages') }}</span>
              </router-link>
            </template>
            <domain-settings
              v-if="(isCompanyAdmin && (hasAccessPropBase || hasAccessMarketBase || hasAccessAdminBase || hasAccessAssetBase || hasAccessAccountingBase || isPlatformEmployee))"
              @domain-deleted="refreshMailboxes" />
            <mailbox-settings
              v-if="isEditor"
              ref="mailboxSettings" />
            <confidential-email-list
              v-if="isCompanyAdmin" />
            <email-font-settings
              v-if="(isCompanyAdmin && (hasAccessPropBase || hasAccessMarketBase || hasAccessAdminBase || hasAccessAssetBase || hasAccessAccountingBase || isPlatformEmployee))" />
            <signature-settings
              v-if="(isCompanyAdmin && (hasAccessPropBase || hasAccessMarketBase || hasAccessAdminBase || hasAccessAssetBase || hasAccessAccountingBase || isPlatformEmployee))" />
            <sender-email-settings
              v-if="(isCompanyAdmin && isTicketEnabled && (hasAccessPropBase || hasAccessMarketBase || hasAccessAdminBase || hasAccessAssetBase || hasAccessAccountingBase || isPlatformEmployee))" />
          </b-tab>
          <b-tab
            v-if="isEditor && (isCompanyAdmin || hasManageTemplatesRole) && !hasAccessUserBase">
            <template #title>
              <router-link
                data-cy="settings-tab-templates"
                :to="getSettingRouteByKey('templates')">
                <span>{{ $t('message.settings.templates') }}</span>
              </router-link>
            </template>
            <template-settings />
            <handover-template-settings />
            <letter-template-settings />
            <ticket-template-settings v-if="isTicketEnabled" />
            <checklist-template-settings v-if="isTicketEnabled && !isOwnerModule" />
            <deep-sign-template-settings v-if="deepSignEnabled" />
            <export-template-settings />
            <contract-template-settings />
          </b-tab>
          <b-tab
            v-if="showTicketingTab">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('ticketing')">
                <span>{{ $t('message.settings.ticketing.title') }}</span>
              </router-link>
            </template>
            <ticketing-settings />
          </b-tab>
          <b-tab
            v-if="isCompanyAdmin">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('reporting')">
                <span>{{ $t('message.settings.reporting.title') }}</span>
              </router-link>
            </template>
            <reporting-settings />
          </b-tab>
          <b-tab
            v-if="isTicketEnabled">
            <template #title>
              <router-link
                data-cy="settings-tab-notifications"
                :to="getSettingRouteByKey('notifications')">
                <span>{{ $t('message.settings.notifications') }}</span>
              </router-link>
            </template>
            <notifications-settings />
          </b-tab>
          <b-tab v-if="!isAssetModule && !isOwnerModule || ((isAssetModule || isOwnerModule) && isCompanyAdmin)">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('integrations')">
                <span>{{ $t('message.settings.deepsign.integration') }}</span>
              </router-link>
            </template>
            <integrations-settings />
          </b-tab>
          <b-tab
            v-if="isCompanyAdmin">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('synchronisation')">
                <span>{{ $t('message.settings.synchronisation.title') }}</span>
              </router-link>
            </template>
            <synchronisation-settings />
          </b-tab>
          <b-tab
            v-if="isCompanyAdmin && !isPlatformEmployee && !isOwnerModule && isAiEnabled">
            <template #title>
              <router-link
                :to="getSettingRouteByKey('ai')">
                <span>{{ $t('message.settings.ai.title') }}</span>
              </router-link>
            </template>
            <ai-settings />
          </b-tab>
        </b-tabs>
      </div>
    </div>
    <b-modal
      ref="modal-confirmation"
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop>
      <div class="row mb-2">
        <div class="col">
          <p class="mb-3">
            {{ $t('message.settings.confirmationText') }}
          </p>
          <coozzy-button
            design="green"
            size="normal"
            @click="discardChanges">
            {{ $t('message.generic.discardChanges') }}
          </coozzy-button>
          <coozzy-button
            class="float-right mr-3 border"
            design="transparent"
            size="normal"
            @click="closeModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import DomainSettings from './domains/DomainSettings'
import CompanyProfileSettings from './components/CompanyProfileSettings'
import EditRegionSettings from './components/EditRegionSettings'
import EditPasswordSettings from './components/EditPasswordSettings'
import EmployeeSettings from './components/EmployeeSettings'
import ExportSettings from './marketing/ExportSettings'
import MailboxSettings from './mailBox/MailboxSettings'
import SignatureSettings from './components/SignatureSettings'
import senderEmailSettings from './components/SenderEmailSettings'
import ViewingAppointmentSenderEmailSettings from '@/settings/marketing/ViewingAppointmentSenderEmailSettings.vue'
import TemplateSettings from './components/TemplateSettings'
import ContractTemplateSettings from '@/settings/components/ContractTemplateSettings'
import { user } from '@/mixins/user'
import IndividualProfileSettings from '@/settings/components/IndividualProfileSettings'
import ApplicationFormSettings from '@/settings/marketing/ApplicationFormSettings'
import AutomaticReferenceCheckSettings from '@/settings/marketing/AutomaticReferenceCheckSettings.vue'
import ApplicationDossierSettings from '@/settings/marketing/ApplicationDossierSettings.vue'
import AffordabilitySettings from '@/settings/marketing/AffordabilitySettings.vue'
import ReferenceEmailTemplatesSettings from '@/settings/marketing/ReferenceEmailTemplatesSettings.vue'
import AppointmentRemindersSettings from '@/settings/marketing/AppointmentRemindersSettings.vue'
import NotificationsSettings from '@/settings/components/NotificationsSettings'
import TicketApi from '@/misc/apis/TicketApi'
import AiGatewayApi from '@/misc/apis/AiGatewayApi'
import { deepSign } from '@/mixins/deepSign'
import { routeChecks } from '@/mixins/routeChecks'
import ExportTemplateSettings from '@/settings/components/ExportTemplateSettings'
import LetterTemplateSettings from '@/settings/components/LetterTemplateSettings.vue'
import TicketTemplateSettings from '@/settings/components/TicketTemplateSettings.vue'
import DeepSignTemplateSettings from '@/settings/components/DeepSignTemplateSettings'
import ConfidentialEmailList from '@/settings/components/ConfidentialEmailList.vue'
import TicketingSettings from '@/settings/components/TicketingSettings.vue'
import EmailFontSettings from '@/settings/components/EmailFontSettings.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import SynchronisationSettings from '@/settings/components/SynchronisationSettings.vue'
import IntegrationsSettings from '@/settings/components/IntegrationsSettings.vue'
import ReportingSettings from '@/settings/components/ReportingSettings.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import HandoverTemplateSettings from '@/settings/components/HandoverTemplateSettings.vue'
import ChecklistTemplateSettings from '@/settings/components/ChecklistTemplateSettings.vue'
import AiSettings from '@/settings/components/AiSettings.vue'
import employee from '@/store/modules/employee'
import { mapActions } from 'vuex'

export default {
  name: 'SettingsView',
  components: {
    HandoverTemplateSettings,
    ChecklistTemplateSettings,
    CoozzyButton,
    ReportingSettings,
    IntegrationsSettings,
    SynchronisationSettings,
    AiSettings,
    CoozzySpinner,
    TicketingSettings,
    ConfidentialEmailList,
    TicketTemplateSettings,
    DeepSignTemplateSettings,
    LetterTemplateSettings,
    NotificationsSettings,
    ApplicationFormSettings,
    AutomaticReferenceCheckSettings,
    ApplicationDossierSettings,
    AffordabilitySettings,
    ReferenceEmailTemplatesSettings,
    AppointmentRemindersSettings,
    IndividualProfileSettings,
    ContractTemplateSettings,
    DomainSettings,
    TemplateSettings,
    EmailFontSettings,
    SignatureSettings,
    MailboxSettings,
    ExportSettings,
    EmployeeSettings,
    EditPasswordSettings,
    EditRegionSettings,
    CompanyProfileSettings,
    ExportTemplateSettings,
    senderEmailSettings,
    ViewingAppointmentSenderEmailSettings
  },
  mixins: [user, deepSign, routeChecks, employee],
  beforeRouteLeave(to, from, next) {
    this.nextPath = next
    this.prevRoute = from
    if (this.hasChanged) {
      this.$refs['modal-confirmation'].show()
    } else {
      this.nextPath()
    }
  },
  props: {
    view: {
      type: String,
      default: '0'
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.settings.title')
    }
  },
  data: () => {
    return {
      nextPath: null,
      prevRoute: null,
      hasChanged: false,
      tabIndex: 0,
      isTicketEnabled: false,
      isAiEnabled: false,
      forceUpdate: false,
      initView: 0,
      settingsLoading: true,
      beforeUnloadHandler: null
    }
  },
  computed: {
    showTicketingTab() {
      if (!this.isTicketEnabled) {
        return false
      }
      if (this.isAdminModule) {
        return this.isCompanyAdmin && this.hasAccessAdminBase
      }
      if (this.isMarketingModule) {
        return this.isCompanyAdmin && this.hasAccessMarketBase
      }
      return false
    },
    visibleSettingTabs() {
      const tabs = []
      if (this.isCompanyAdmin) {
        tabs.push('general')
      }
      if ((this.hasAccessMarketBase || this.isPlatformEmployee) && this.isEditor) {
        tabs.push('marketBase')
      }
      tabs.push('employees')
      if (this.isEditor || (this.isCompanyAdmin && (this.hasAccessPropBase || this.hasAccessMarketBase || this.hasAccessAdminBase || this.hasAccessAssetBase || this.hasAccessAccountingBase || this.isPlatformEmployee))) {
        tabs.push('messages')
      }
      if (this.isEditor && (this.isCompanyAdmin || this.hasManageTemplatesRole) && !this.hasAccessUserBase) {
        tabs.push('templates')
      }
      if (this.showTicketingTab) {
        tabs.push('ticketing')
      }
      if (this.isCompanyAdmin) {
        tabs.push('reporting')
      }
      if (this.isTicketEnabled) {
        tabs.push('notifications')
      }
      if ((!this.isAssetModule && !this.isOwnerModule) || ((this.isAssetModule || this.isOwnerModule) && this.isCompanyAdmin)) {
        tabs.push('integrations')
      }
      if (this.isCompanyAdmin) {
        tabs.push('synchronisation')
      }
      if (this.isCompanyAdmin && !this.isPlatformEmployee && !this.isOwnerModule && this.isAiEnabled) {
        tabs.push('ai')
      }
      return tabs
    },
    maxVisibleTabIndex() {
      return Math.max(0, this.visibleSettingTabs.length - 1)
    }
  },
  watch: {
    tabIndex: function (newVal) {
      if ((!this.$route.query || parseInt(this.$route.query.view || 0) !== parseInt(newVal)) && this.forceUpdate) {
        if (this.$route.name === 'MarketingSettingsView') {
          this.$router.replace({ name: 'MarketingSettingsView', query: { view: '' + newVal } })
        } else if (this.$route.name === 'AdminSettingsView') {
          this.$router.replace({ name: 'AdminSettingsView', query: { view: '' + newVal } })
        } else if (this.$route.name === 'AssetSettingsView') {
          this.$router.replace({ name: 'AssetSettingsView', query: { view: '' + newVal } })
        } else if (this.$route.name === 'AccountingSettingsView') {
          this.$router.replace({ name: 'AccountingSettingsView', query: { view: '' + newVal } })
        } else {
          this.$router.replace({ name: 'OwnerSettingsView', query: { view: '' + newVal } })
        }
      }
    }
  },
  mounted() {
    this.beforeUnloadHandler = (event) => {
      if (this.hasChanged) {
        event.returnValue = this.$t('message.generic.discardChanges')
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    Promise.all([
      TicketApi.hasTicketingEnabled(this.accountId)
        .then(response => {
          this.isTicketEnabled = response.enabled
        })
        .catch(e => {
          console.error(e)
          this.isTicketEnabled = false
        }),
      AiGatewayApi.getAccountSettings(String(this.accountId))
        .then(response => {
          this.isAiEnabled = !!response.settings?.enabled
        })
        .catch(e => {
          console.error(e)
          this.isAiEnabled = false
        })
    ]).then(() => {
      this.forceUpdate = true
      this.initView = this.normalizeViewIndex(this.view)
      setTimeout(() => {
        this.tabIndex = this.initView
        this.settingsLoading = false
      }, 700)
    })
    this.loadEmployees()
  },
  beforeDestroy() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    hasChangedEdited(hasChanged) {
      this.hasChanged = hasChanged
    },
    closeModal() {
      if (this.$refs['modal-confirmation']) {
        this.$refs['modal-confirmation'].hide()
      }
    },
    discardChanges() {
      this.closeModal()
      if (this.nextPath !== null) {
        this.nextPath()
      } else {
        this.$router.go(-1)
      }
    },
    refreshMailboxes() {
      this.$refs.mailboxSettings.getMailBoxList()
    },
    normalizeViewIndex(view) {
      const parsedView = Number(view)
      if (Number.isNaN(parsedView) || parsedView < 0) {
        return 0
      }
      if (parsedView > this.maxVisibleTabIndex) {
        return this.maxVisibleTabIndex
      }
      return parsedView
    },
    getSettingTabIndex(tabKey) {
      const tabIndex = this.visibleSettingTabs.indexOf(tabKey)
      return tabIndex >= 0 ? tabIndex : 0
    },
    getSettingRouteByKey(tabKey) {
      const index = this.getSettingTabIndex(tabKey)
      if (this.isOwnerModule) {
        return { name: 'OwnerSettingsView', query: { view: '' + index } }
      } else if (this.isAdminModule) {
        return { name: 'AdminSettingsView', query: { view: '' + index } }
      } else if (this.isAssetModule) {
        return { name: 'AssetSettingsView', query: { view: '' + index } }
      } else if (this.isAccountingModule) {
        return { name: 'AccountingSettingsView', query: { view: '' + index } }
      } else {
        return { name: 'MarketingSettingsView', query: { view: '' + index } }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  :deep(.tabs .nav-tabs .nav-link a.router-link-active), :deep(.tabs .nav-tabs .nav-link a) {
    color: inherit !important;
    text-decoration: none !important;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation]) {
    margin-right: 0.2rem;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item > a.nav-link) {
    margin-right: 0;
    padding: 0;
    box-shadow: none;
    border: 0;
    z-index: -1;
    height: 26.5px;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link.active > a) {
    font-weight: bold;
    background-color: #fff;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link:hover > a) {
    font-weight: bold;
    cursor: pointer;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link > a) {
    color: rgb(0, 68, 95) !important;
    border-color: #c8ced3 !important;
    border-radius: 0 !important;
    border-bottom: 0 !important;
    margin-right: 0;
    padding: 0.55rem 1.5rem;
    font-weight: 300;
    -webkit-box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    cursor: default;
    border: 1px solid transparent;
    height: 36.5px;
  }
  :deep(.card.shadow) {
    box-shadow: none !important;
  }
  .tab-content>.active {
    background: #fff;
    position: relative;
  }
</style>
